import { Option, Step } from "@web/components/Forms/types"
import { extensionId } from "@web/config/next"
import { VacancyForm } from "./CreateVacancyForm.types"
import { BrowserMessageType, GQL } from "platform-common/index"
import { Vertical } from "platform-common/types/vertical"
import { YES, YesNo, nameAndIdToOption } from "../Forms"
import type { UpsertVacancyArgs, VacancyInitialValues } from "./CreateVacancyForm"
import { NEGATIVE_OPC, POSITIVE_OPC } from "platform-common/OPC"
import { isField, isWrappedFields } from "../Forms/Form"
import { stripHtml } from "string-strip-html"
import { flatten, pick } from "lodash"
import { UseFormReturn } from "react-hook-form"
import { fixVacancyUrl } from "./fixVacancyUrl"
import { jobTitleToJobTitleOption } from "./JobTitleSelector/JobTitleSelector.utils"
import { FetchResponseSerialized } from "platform-common/util/jobUrl"

export type SerializedVacancyData = ReturnType<typeof serializeVacancyForm>

export const DEFAULT_POSITION_TYPE: GQL.PositionType = "CLIENT"

export const getVerticalOption = (vertical: Vertical, enabled = true): Option<Vertical> => {
  const suffix = enabled ? "" : " (Coming Soon)"

  switch (vertical) {
    case "SOFTWARE":
      return { value: vertical, label: `IT/Software/Hardware${suffix}` }
    case "ACCOUNTING":
      return { value: vertical, label: `Accounting${suffix}` }
    case "EXECUTIVE":
      return { value: vertical, label: `Executive${suffix}` }
    case "EDUCATION":
      return { value: vertical, label: `Education${suffix}` }
    case "CUSTOMER_SERVICE":
      return { value: vertical, label: `Customer Service/Retail${suffix}` }
    case "HEALTHCARE":
      return { value: vertical, label: `Nursing/Healthcare${suffix}` }
    case "RECRUITING":
      return { value: vertical, label: `Recruiting${suffix}` }
    case "LEGAL":
      return { value: vertical, label: `Legal${suffix}` }
    case "CONSTRUCTION":
      return { value: vertical, label: `Construction${suffix}` }
    case "LIGHT_INDUSTRIAL":
      return { value: vertical, label: `Light Industrial${suffix}` }
    case "MANUFACTORING":
      return { value: vertical, label: `Manufacturing${suffix}` }
    case "SALES":
      return { value: vertical, label: `Sales & Marketing${suffix}` }
    default:
      return { value: "SOFTWARE", label: `Software${suffix}` }
  }
}

export const getWageTypeOption = (wageType: GQL.WageType): Option<GQL.WageType> => {
  switch (wageType) {
    case "HOURLY":
      return { label: "Hour", value: "HOURLY" }
    case "MONTHLY":
      return { label: "Month", value: "MONTHLY" }
    case "YEARLY":
      return { label: "Year", value: "YEARLY" }
    default:
      return { label: "Hour", value: "HOURLY" }
  }
}

export const getLocationTypeOption = (locationType: GQL.LocationType): Option<GQL.LocationType> => {
  switch (locationType) {
    case "REMOTE":
      return { label: "Remote", value: "REMOTE" }
    case "ONSITE":
      return { label: "On-site", value: "ONSITE" }
    case "HYBRID":
      return { label: "Hybrid", value: "HYBRID" }
    default:
      return { label: "Remote", value: "REMOTE" }
  }
}

export const getPositionForOption = (positionFor: GQL.PositionFor): Option<GQL.PositionFor> => {
  switch (positionFor) {
    case "FOR_A_GLOBAL_BRAND":
      return { label: "A global brand", value: "FOR_A_GLOBAL_BRAND" }
    case "DISRUPTIVE_INDUSTRY_PLAYER":
      return { label: "A disruptive industry player", value: "DISRUPTIVE_INDUSTRY_PLAYER" }
    case "ESTABLISHED_INDUSTRY_PLAYER":
      return { label: "An established industry player", value: "ESTABLISHED_INDUSTRY_PLAYER" }
    case "WELL_FUNDED_NEW_COMPANY":
      return { label: "A well funded new company", value: "WELL_FUNDED_NEW_COMPANY" }

    case "AN_EXCITING_NEW_FIRM":
      return { label: "An exciting new firm", value: "AN_EXCITING_NEW_FIRM" }
    case "A_WELL_ESTABLISHED_FIRM":
      return { label: "A well established firm", value: "A_WELL_ESTABLISHED_FIRM" }
    case "A_LEADING_FIRM_IN_THEIR_SPECIALTY":
      return { label: "A leading firm in their specialty", value: "A_LEADING_FIRM_IN_THEIR_SPECIALTY" }
    case "WELL_KNOWN_LEGAL_BRAND":
      return { label: "A well known legal brand", value: "WELL_KNOWN_LEGAL_BRAND" }

    case "WELL_FINANCED_STARTUP":
      return { label: "A well financed startup", value: "WELL_FINANCED_STARTUP" }
    case "ESTABLISHED_COMPANY":
      return { label: "An established company", value: "ESTABLISHED_COMPANY" }
    case "MAJOR_DISTRIBUTOR_IN_THEIR_SPACE":
      return { label: "A major distributor in their space", value: "MAJOR_DISTRIBUTOR_IN_THEIR_SPACE" }
    case "STARTUP_ROCKETSHIP":
      return { label: "A startup rocketship", value: "STARTUP_ROCKETSHIP" }

    default:
      return { label: "A global brand", value: "FOR_A_GLOBAL_BRAND" }
  }
}

export const getYesNoOption = (yesNo: boolean): Option<YesNo> => {
  return yesNo ? { label: "Yes", value: YES } : { label: "No", value: "NO" }
}

export const getBrandOption = (brand: GQL.BrandType): Option<GQL.BrandType> => {
  switch (brand) {
    case "LUXURY":
      return { label: "Luxury Brand", value: "LUXURY" }
    case "GLOBAL":
      return { label: "Global Brand", value: "GLOBAL" }
    case "UPSTART":
      return { label: "Upstart Brand", value: "UPSTART" }
    default:
      return { label: "Upstart Brand", value: "UPSTART" }
  }
}

export const getCommissionPlanOption = (commissionPlan: GQL.CommissionPlanType): Option<GQL.CommissionPlanType> => {
  switch (commissionPlan) {
    case "COMPETITIVE":
      return { label: "Competitive", value: "COMPETITIVE" }
    case "VERY_COMPETITIVE":
      return { label: "Very Competitive", value: "VERY_COMPETITIVE" }
    case "UNCAPPED":
      return { label: "Uncapped", value: "UNCAPPED" }
    default:
      return { label: "Competitive", value: "COMPETITIVE" }
  }
}

export const getLightIndustrialProdTypeOption = (
  lightIndustrialProdType: GQL.LightIndustrialProdType
): Option<GQL.LightIndustrialProdType> => {
  switch (lightIndustrialProdType) {
    case "INNOVATIVE":
      return { label: "An innovative product", value: "INNOVATIVE" }
    case "DEFENSE":
      return { label: "A product that defends our country", value: "DEFENSE" }
    case "LIVE_CHANGING":
      return { label: "A product that makes a difference in people’s lives", value: "LIVE_CHANGING" }
    default:
      return { label: "An innovative product", value: "INNOVATIVE" }
  }
}

export const getPayDescriptionOption = (payDescription: GQL.PayDescription): Option<GQL.PayDescription> => {
  switch (payDescription) {
    case "INDUSTRY_AVERAGE_WAGE":
      return { label: "Industry average wage", value: "INDUSTRY_AVERAGE_WAGE" }
    case "INDUSTRY_AVERAGE_PACKAGE":
      return { label: "Industry average package", value: "INDUSTRY_AVERAGE_PACKAGE" }
    case "ABOVE_INDUSTRY_AVERAGE_WAGE":
      return { label: "Above industry average wage", value: "ABOVE_INDUSTRY_AVERAGE_WAGE" }
    case "ABOVE_INDUSTRY_AVERAGE_PACKAGE":
      return { label: "Above industry average package", value: "ABOVE_INDUSTRY_AVERAGE_PACKAGE" }
    case "COMPETITIVE_WAGE":
      return { label: "Competitive wage", value: "COMPETITIVE_WAGE" }
    case "COMPETITIVE_PACKAGE":
      return { label: "Competitive package", value: "COMPETITIVE_PACKAGE" }
    case "VERY_COMPETITIVE_WAGE":
      return { label: "Very competitive wage", value: "VERY_COMPETITIVE_WAGE" }
    case "VERY_COMPETITIVE_PACKAGE":
      return { label: "Very competitive package", value: "VERY_COMPETITIVE_PACKAGE" }
    case "TOP_OF_THE_MARKET_WAGE":
      return { label: "Top of the market wage", value: "TOP_OF_THE_MARKET_WAGE" }
    case "TOP_OF_THE_MARKET_PACKAGE":
      return { label: "Top of the market package", value: "TOP_OF_THE_MARKET_PACKAGE" }
    default:
      return { label: "Competitive wage", value: "COMPETITIVE_WAGE" }
  }
}

export const getPositionTypeOption = (positionType: GQL.PositionType): Option<GQL.PositionType> => {
  switch (positionType) {
    case "CLIENT":
      return { label: "Client", value: "CLIENT" }

    case "INTERNAL":
      return { label: "In-House Position", value: "INTERNAL" }

    default:
      return { label: "In-House Position", value: "INTERNAL" }
  }
}

export const getEmploymentTypeOption = (employmentType: GQL.EmploymentType): Option<GQL.EmploymentType> => {
  switch (employmentType) {
    case "FULL_TIME":
      return { label: "Full Time", value: "FULL_TIME" }
    case "PART_TIME":
      return { label: "Part Time", value: "PART_TIME" }
    case "BOTH":
      return { label: "Both", value: "BOTH" }
    default:
      return { label: "Full Time", value: "FULL_TIME" }
  }
}

export const USER_CREATED_OPTION_ID_PREFIX = "LOCAL-PREFIX"

export const POSITIVE_OPC_OPTIONS = POSITIVE_OPC.map((opc) => ({ value: opc, label: opc }))

export const NEGATIVE_OPC_OPTIONS = NEGATIVE_OPC.map((opc) => ({ value: opc, label: opc }))

/**
 * Remove the ID if it's a user-created option
 */
export const optionToSkill = (option: Option) => ({
  name: option.label,
  id: option.value.startsWith(USER_CREATED_OPTION_ID_PREFIX) ? undefined : option.value,
})

/**
 * Remove the ID if it's a user-created option
 */
export const optionToJobTitle = (option: Option) => ({
  title: option.label,
  id: option.value.startsWith(USER_CREATED_OPTION_ID_PREFIX) ? undefined : option.value,
})

/**
 * Only keep the fields that are relevant to the vertical
 */
export const filterFormValues = ({ form, forceLocationType }: SerializeVacancyFormArgs): Partial<VacancyForm> => {
  const vertical = form.vertical?.value

  const accountingFields: (keyof VacancyForm)[] = [
    "locationType",
    "payRange",
    "wageType",
    "clientOriginationBonus",
    "benefits",
  ]

  const constructionFields: (keyof VacancyForm)[] = ["payRange", "wageType", "signOnBonus", "signOnBonusValue"]

  const customerServiceFields: (keyof VacancyForm)[] = [
    "payRange",
    "wageType",
    "brandType",
    "brandName",
    "commissionPlan",
  ]

  const executiveFields: (keyof VacancyForm)[] = [
    "payRange",
    "wageType",
    "benefits",
    "equityOptionsOffered",
    "innovativeProduct",
    "scaleExistingProduct",
    "buildTeam",
    "positionFor",
  ]

  const softwareFields: (keyof VacancyForm)[] = ["payRange", "wageType", "locationType", "buzzwordsCloud"]

  const educationFields: (keyof VacancyForm)[] = [
    "allowedToDisclosePayRange",
    "payRange",
    "wageType",
    "signOnBonus",
    "signOnBonusValue",
    "paidSickLeave",
    "hasGrowthOpportunities",
    "typicalWorkingHours",
    "payDescription",
  ]

  const lightIndustrialFields: (keyof VacancyForm)[] = [
    "payRange",
    "wageType",
    "apprenticeshipProgram",
    "advancementOpportunity",
    "lightIndustrialProdType",
  ]

  const manufacturingFields: (keyof VacancyForm)[] = [
    "payRange",
    "wageType",
    "signOnBonus",
    "signOnBonusValue",
    "apprenticeshipProgram",
    "advancementOpportunity",
    "lightIndustrialProdType",
    "nightShiftPayDifferential",
  ]

  const legalFields: (keyof VacancyForm)[] = [
    "payRange",
    "wageType",
    "signOnBonus",
    "signOnBonusValue",
    "autonomy",
    "buildLitigationSkills",
    "clientContact",
    "positionFor",
    "locationType",
  ]

  const healthcareFields: (keyof VacancyForm)[] = [
    "allowedToDisclosePayRange",
    "payRange",
    "wageType",
    "signOnBonus",
    "signOnBonusValue",
    "relocationAssistance",
    "employmentType",
    "payDescription",
  ]

  return {
    // Generic fields
    name: form.name,
    jobTitle: form.jobTitle,
    vertical: form.vertical,
    mustHaveSkills: form.mustHaveSkills,
    niceToHaveSkills: form.niceToHaveSkills,
    benefits: form.benefits,
    atsLink: form.atsLink,
    location: form.location,
    benefitsTemplateFragment: form.benefitsTemplateFragment,
    responsibilities: form.responsibilities,
    positiveOPCs: form.positiveOPCs,
    negativeOPCs: form.negativeOPCs,
    isStackRankRequired: form.isStackRankRequired,
    schedulingLink: form.schedulingLink,
    phoneNumber: form.phoneNumber,
    recruitmentType: form.recruitmentType,
    companyName: form.companyName,
    companyUrl: form.companyUrl,
    revealCompany: form.revealCompany,
    phoneNumberAcceptsText: form.phoneNumberAcceptsText,
    thirdPartyLinkURL: form.thirdPartyLinkURL,
    thirdPartyLinkText: form.thirdPartyLinkText,
    organizationTypeDescription: form.organizationTypeDescription,
    idealCandidate: form.idealCandidate,
    keywords: form.keywords,

    locationType: forceLocationType ? form.locationType : undefined,
    address: form.locationType?.value === "ONSITE" ? form.address : undefined,

    // Vertical-specific fields
    ...(vertical === "ACCOUNTING" && pick(form, accountingFields)),
    ...(vertical === "CONSTRUCTION" && pick(form, constructionFields)),
    ...(vertical === "CUSTOMER_SERVICE" && pick(form, customerServiceFields)),
    ...(vertical === "EXECUTIVE" && pick(form, executiveFields)),
    ...(vertical === "SOFTWARE" && pick(form, softwareFields)),
    ...(vertical === "EDUCATION" && pick(form, educationFields)),
    ...(vertical === "LIGHT_INDUSTRIAL" && pick(form, lightIndustrialFields)),
    ...(vertical === "MANUFACTORING" && pick(form, manufacturingFields)),
    ...(vertical === "LEGAL" && pick(form, legalFields)),
    ...(vertical === "HEALTHCARE" && pick(form, healthcareFields)),
  }
}

interface SerializeVacancyFormArgs {
  form: Partial<VacancyForm>
  vacancyId?: string
  forceLocationType?: GQL.LocationType // keep the location type regardless of the vertical?
}

export type VacancyFormOutput = {
  serialized: UpsertVacancyArgs
  form: Partial<VacancyForm>
}

export const serializeVacancyForm = ({ form: form_, vacancyId, forceLocationType }: SerializeVacancyFormArgs) => {
  const vertical = form_.vertical?.value

  const form = filterFormValues({ form: form_, forceLocationType })

  const requiredSkills = form.mustHaveSkills?.map((s) => ({
    skill: optionToSkill(s),
    isRequired: true,
    yearsOfExperience: s.data?.yearsOfExperience,
  }))

  const niceToHaveSkills = form.niceToHaveSkills?.map((s) => ({
    skill: optionToSkill(s),
    isRequired: false,
    yearsOfExperience: s.data?.yearsOfExperience,
  }))

  const serialized = {
    vacancyId,
    data: {
      ...EMPTY_VACANCY_INPUT, // zero-out all fields that will not be sent

      name: form.name?.trim() || null,
      jobTitleId: form.jobTitle ? optionToSkill(form.jobTitle).id : null,
      jobTitleFull: form.jobTitle ? optionToJobTitle(form.jobTitle) : null,
      correlatedJobTitleIds: form.jobTitle?.data?.correlatedJobTitles.map((j) => j.id) || [],
      vertical: vertical || "ANY",
      skills: requiredSkills?.concat(niceToHaveSkills || []) || [],
      atsLink: fixVacancyUrl(form?.atsLink || "")?.trim() || null,
      location: form.location?.map((l) => ({ country: l })) || [],
      benefitsTemplateFragment: form.benefitsTemplateFragment?.trim() || null || null,
      responsibilitiesFragment: form.responsibilities?.trim() || null || null,
      positiveOPCs: form.positiveOPCs?.map((o) => o.value) || [],
      negativeOPCs: form.negativeOPCs?.map((o) => o.value) || [],
      isStackRankRequired: form.isStackRankRequired != undefined ? form.isStackRankRequired : null,
      schedulingLink: form.schedulingLink?.trim() || null,
      phoneNumber: form.phoneNumber?.trim() || null,
      recruitmentType: form.recruitmentType?.value,
      payDescription: form.payDescription?.value || null,
      companyName: form.companyName?.trim() || null,
      companyUrl: form.companyUrl?.trim() || null,
      revealCompany: form.recruitmentType?.value === "CLIENT" ? form.revealCompany : null,
      phoneNumberAcceptsText: form.phoneNumber?.trim() ? form.phoneNumberAcceptsText : null,
      thirdPartyLinkURL: form.thirdPartyLinkURL?.trim() || null,
      thirdPartyLinkText: form.thirdPartyLinkText?.trim() || null,
      organizationTypeDescription: form.organizationTypeDescription?.value || null,
      locationType: form.locationType?.value || null,
      clientOriginationBonus: form.clientOriginationBonus ? form.clientOriginationBonus.value === "YES" : null,
      benefits: form.benefits?.map((b) => b.value) || [],
      signOnBonus: form.signOnBonus ? form.signOnBonus?.value === YES : null,
      signOnBonusValue: parseInt(form.signOnBonusValue?.replace(/[^\d]/g, "") || "0") || null,
      brandType: form.forKnownBrand && form.brandType?.value ? [form.brandType.value] : [],
      brandName: form.forKnownBrand ? form.brandName?.trim() || null : null,
      commissionPlan: form.hasCommissionPlan?.value === "YES" ? form.commissionPlan?.value : null,
      equityOptionsOffered: form.equityOptionsOffered ? form.equityOptionsOffered.value === "YES" : null,
      innovativeProduct: form.innovativeProduct ? form.innovativeProduct.value === "YES" : null,
      scaleExistingProduct: form.scaleExistingProduct ? form.scaleExistingProduct.value === "YES" : null,
      buildTeam: form.buildTeam ? form.buildTeam.value === "YES" : null,
      positionFor: form.positionFor?.value ? [form.positionFor.value] : null,
      allowedToDisclosePayRange: form.allowedToDisclosePayRange?.value === YES,
      buzzwordsCloud: form.buzzwordsCloud?.map((o) => o.value) || [],
      paidSickLeave: form.paidSickLeave ? form.paidSickLeave.value === "YES" : null,
      hasGrowthOpportunities: form.hasGrowthOpportunities ? form.hasGrowthOpportunities.value === "YES" : null,
      typicalWorkingHours:
        form.typicalWorkingHours?.value === "OTHER"
          ? form.customWorkingHours?.trim() || null
          : form.typicalWorkingHours?.value || null,
      apprenticeshipProgram: form.apprenticeshipProgram ? form.apprenticeshipProgram.value === "YES" : null,
      advancementOpportunity: form.advancementOpportunity ? form.advancementOpportunity.value === "YES" : null,
      lightIndustrialProdType: form.lightIndustrialProdType?.value || null,
      nightShiftPayDifferential: form.nightShiftPayDifferential
        ? form.nightShiftPayDifferential.value === "YES"
          ? "Yes"
          : "No"
        : null,
      autonomy: form.autonomy ? form.autonomy.value === "YES" : null,
      buildLitigationSkills: form.buildLitigationSkills ? form.buildLitigationSkills.value === "YES" : null,
      clientContact: form.clientContact ? form.clientContact.value === "YES" : null,
      relocationAssistance: form.relocationAssistance ? form.relocationAssistance.value === "YES" : null,
      employmentType: form.employmentType?.value || null,
      productInfo: form.productInfo?.value || null,
      salesOpportunityInfo: form.salesOpportunityInfo?.value || null,
      compPlanInfo: form.compPlanInfo?.value || null,
      idealCandidateProfile: form.idealCandidate?.trim() || null,

      ...(["SALES", "EDUCATION", "HEALTHCARE"].includes(vertical!)
        ? {
            payRange:
              form.allowedToDisclosePayRange?.value === YES
                ? form.payRange?.trim() || null
                : form.wageQuality?.value || null,
            wageType: form.allowedToDisclosePayRange?.value === YES ? form.wageType?.value || null : null,
          }
        : {
            payRange: form.payRange?.trim() || null,
            wageType: form.wageType?.value || null,
          }),
    },
  } satisfies UpsertVacancyArgs

  return { serialized, form }
}

interface GetVacancyFormInitialValuesArgs {
  vacancy?: VacancyInitialValues | null
  defaultVertical?: Vertical
}

export const getVacancyFormInitialValues = ({
  vacancy,
  defaultVertical,
}: GetVacancyFormInitialValuesArgs): Partial<VacancyForm> => {
  return {
    vertical: vacancy?.vertical
      ? getVerticalOption(vacancy.vertical)
      : defaultVertical
        ? getVerticalOption(defaultVertical)
        : undefined,
    name: vacancy?.name || "",
    description: vacancy?.description || "",
    jobTitle: vacancy?.jobTitleFull ? jobTitleToJobTitleOption(vacancy.jobTitleFull) : undefined,
    atsLink: vacancy?.atsLink || "",
    mustHaveSkills: vacancy?.skills
      ?.filter((s) => s.isRequired)
      .map((s) => ({
        ...nameAndIdToOption(s.skill),

        // FIXME - these should come from search results
        data: {
          yearsOfExperience: s.yearsOfExperience || 1,
          maxYearsOfExperience: undefined, // FIXME - get this from somewhere
          description: undefined, // FIXME - get this from somewhere
        },
      })),
    niceToHaveSkills: vacancy?.skills
      ?.filter((s) => !s.isRequired)
      .map((s) => ({
        ...nameAndIdToOption(s.skill),

        // FIXME - these should come from search results
        data: {
          yearsOfExperience: s.yearsOfExperience || 1,
          maxYearsOfExperience: undefined, // FIXME - get this from somewhere
          description: undefined, // FIXME - get this from somewhere
        },
      })),
    location: vacancy?.location?.map((l) => l.country) || [],
    companyPitch: vacancy?.elevatorPitch || "",
    benefitsTemplateFragment: vacancy?.benefitsTemplateFragment || "",
    responsibilities: vacancy?.responsibilitiesFragment || "",
    disclosure: vacancy?.disclosureTemplateFragment || "",
    positiveOPCs: vacancy?.positiveOPCs?.map((o) => ({ label: o, value: o })) || POSITIVE_OPC_OPTIONS,
    negativeOPCs: vacancy?.negativeOPCs?.map((o) => ({ label: o, value: o })) || NEGATIVE_OPC_OPTIONS,
    isStackRankRequired: vacancy?.isStackRankRequired || false,
    companyName: vacancy?.companyName || "",
    companyUrl: vacancy?.companyUrl || "",
    revealCompany: vacancy?.revealCompany || false,
    phoneNumber: vacancy?.phoneNumber || undefined,
    phoneNumberAcceptsText: vacancy?.phoneNumberAcceptsText || undefined,
    recruitmentType: getPositionTypeOption(vacancy?.recruitmentType || DEFAULT_POSITION_TYPE),
    schedulingLink: vacancy?.schedulingLink || "",
    keywords: vacancy?.keywords?.map((k) => ({ label: k, value: k })) || [],
    idealCandidate: vacancy?.idealCandidateProfile || "",
    payRange:
      vacancy?.allowedToDisclosePayRange != undefined
        ? vacancy.allowedToDisclosePayRange
          ? vacancy?.payRange
          : ""
        : vacancy?.payRange || "",
    forKnownBrand: getYesNoOption(!!vacancy?.brandType?.length),
    wageType: vacancy?.wageType ? getWageTypeOption(vacancy.wageType) : undefined,
    clientOriginationBonus:
      vacancy?.clientOriginationBonus != undefined
        ? getYesNoOption(vacancy.clientOriginationBonus)
        : getYesNoOption(false),
    benefits: vacancy?.benefits?.map((b) => ({ label: b, value: b })) || [],
    locationType: vacancy?.locationType ? getLocationTypeOption(vacancy.locationType) : undefined,
    signOnBonus: vacancy?.signOnBonus != undefined ? getYesNoOption(vacancy.signOnBonus) : getYesNoOption(false),
    signOnBonusValue: vacancy?.signOnBonusValue ? `$${vacancy?.signOnBonusValue.toLocaleString()}` : "",
    brandType: vacancy?.brandType?.[0] ? getBrandOption(vacancy.brandType[0]) : undefined,
    brandName: vacancy?.brandName || "",
    commissionPlan: vacancy?.commissionPlan ? getCommissionPlanOption(vacancy.commissionPlan) : undefined,
    hasCommissionPlan: getYesNoOption(vacancy?.commissionPlan != undefined),
    equityOptionsOffered:
      vacancy?.equityOptionsOffered != undefined ? getYesNoOption(vacancy.equityOptionsOffered) : getYesNoOption(false),
    innovativeProduct:
      vacancy?.innovativeProduct != undefined ? getYesNoOption(vacancy.innovativeProduct) : getYesNoOption(false),
    scaleExistingProduct:
      vacancy?.scaleExistingProduct != undefined ? getYesNoOption(vacancy.scaleExistingProduct) : getYesNoOption(false),
    buildTeam: vacancy?.buildTeam != undefined ? getYesNoOption(vacancy.buildTeam) : getYesNoOption(false),
    buzzwordsCloud: vacancy?.buzzwordsCloud?.map((b) => ({ label: b, value: b })) || [],
    paidSickLeave: vacancy?.paidSickLeave != undefined ? getYesNoOption(vacancy.paidSickLeave) : getYesNoOption(false),
    hasGrowthOpportunities:
      vacancy?.hasGrowthOpportunities != undefined ? getYesNoOption(vacancy.hasGrowthOpportunities) : undefined,
    typicalWorkingHours: vacancy?.typicalWorkingHours
      ? { label: vacancy.typicalWorkingHours, value: vacancy.typicalWorkingHours }
      : undefined,
    customWorkingHours: vacancy?.typicalWorkingHours === "OTHER" ? vacancy.typicalWorkingHours : undefined,
    apprenticeshipProgram:
      vacancy?.apprenticeshipProgram != undefined
        ? getYesNoOption(vacancy.apprenticeshipProgram)
        : getYesNoOption(false),
    advancementOpportunity:
      vacancy?.advancementOpportunity != undefined
        ? getYesNoOption(vacancy.advancementOpportunity)
        : getYesNoOption(false),
    lightIndustrialProdType: vacancy?.lightIndustrialProdType
      ? getLightIndustrialProdTypeOption(vacancy.lightIndustrialProdType)
      : undefined,
    autonomy: vacancy?.autonomy ? getYesNoOption(vacancy.autonomy) : undefined,
    buildLitigationSkills:
      vacancy?.buildLitigationSkills != undefined ? getYesNoOption(vacancy.buildLitigationSkills) : undefined,
    clientContact: vacancy?.clientContact ? getYesNoOption(vacancy.clientContact) : undefined,
    allowedToDisclosePayRange:
      vacancy?.allowedToDisclosePayRange != undefined
        ? getYesNoOption(vacancy.allowedToDisclosePayRange)
        : getYesNoOption(false),
    wageQuality:
      vacancy?.allowedToDisclosePayRange || !["EDUCATION", "HEALTHCARE"].includes(vacancy?.vertical as GQL.Vertical)
        ? undefined
        : { label: vacancy?.payRange || "", value: vacancy?.payRange || "" },
    relocationAssistance:
      vacancy?.relocationAssistance != undefined ? getYesNoOption(vacancy.relocationAssistance) : undefined,
    employmentType: vacancy?.employmentType ? getEmploymentTypeOption(vacancy.employmentType) : undefined,
    positionFor: vacancy?.positionFor?.[0] ? getPositionForOption(vacancy.positionFor[0]) : undefined,
    payDescription: vacancy?.payDescription ? getPayDescriptionOption(vacancy.payDescription) : undefined,
    organizationTypeDescription: vacancy?.organizationTypeDescription
      ? { label: vacancy.organizationTypeDescription, value: vacancy.organizationTypeDescription }
      : undefined,
    thirdPartyLinkURL: vacancy?.thirdPartyLinkURL || undefined,
    thirdPartyLinkText: vacancy?.thirdPartyLinkText || undefined,
    compPlanInfo:
      vacancy?.compPlanInfo != undefined ? { label: vacancy.compPlanInfo, value: vacancy.compPlanInfo } : undefined,
    salesOpportunityInfo:
      vacancy?.salesOpportunityInfo != undefined
        ? { label: vacancy.salesOpportunityInfo, value: vacancy.salesOpportunityInfo }
        : undefined,
    productInfo:
      vacancy?.productInfo != undefined ? { label: vacancy.productInfo, value: vacancy.productInfo } : undefined,
  }
}

export const sendExtensionMessage = (
  messageType: BrowserMessageType,
  message?: any
): Promise<FetchResponseSerialized | null> => {
  if (!chrome.runtime) return Promise.resolve(null)

  if (!chrome.runtime.sendMessage || typeof chrome.runtime.sendMessage !== "function") {
    console.debug("sendMessage is not not available. Couldn't send extension message.")
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    try {
      chrome.runtime.sendMessage(extensionId, { message: messageType, ...message }, (res) => {
        resolve(res)
      })
    } catch (error) {
      console.error(error)
      return resolve(null)
    }
  })
}

export const hasTwoStepsSkippedInRow = (form: UseFormReturn<VacancyForm, any>, previousSteps: Step<VacancyForm>[]) => {
  const skips = previousSteps.map((ps) => {
    if (ps.completionCondition == "no-fields-required") return false

    const fields = flatten(ps.fields?.map((f) => (isWrappedFields(f) ? f.fields.filter(isField) : f)) || [])
    const fieldsWithNoValue = fields.filter((f) => {
      const value: any = form.getValues(f.name as keyof VacancyForm)

      if (Array.isArray(value)) return !value.length

      if (typeof value === "string") return !stripHtml(value).result.trim()

      return value == undefined
    })

    // step is considered skipped if all fields are empty
    if (ps.completionCondition == "at-least-one-field") {
      return fieldsWithNoValue.length == fields.length
    }

    // otherwise it's considered skipped if at least one field is empty
    return fieldsWithNoValue.length > 0
  })

  let isPrevSkipped = false

  for (const [i, skip] of skips.entries()) {
    if (skip && isPrevSkipped) {
      return true
    }

    isPrevSkipped = skip
  }

  return false
}

const EMPTY_VACANCY_INPUT: GQL.UpdateVacancyDataInput = {
  name: null,
  jobTitleFull: null,
  atsLink: null,
  clientName: null,
  companyName: null,
  companyUrl: null,
  revealCompany: null,
  location: null,
  vacancyStatus: null,
  skills: null,
  requiredCertificates: null,
  previousIndustries: null,
  aiChecks: null,
  vertical: null,
  elevatorPitch: null,
  benefitsTemplateFragment: null,
  responsibilitiesFragment: null,
  disclosureTemplateFragment: null,
  positiveOPCs: null,
  negativeOPCs: null,
  isStackRankRequired: null,
  shouldSearchCandidates: null,
  phoneNumber: null,
  phoneNumberAcceptsText: null,
  recruitmentType: null,
  schedulingLink: null,
  positionFor: null,
  payRange: null,
  wageType: null,
  benefits: null,
  clientOriginationBonus: null,
  locationType: null,
  allowedToDisclosePayRange: null,
  signOnBonus: null,
  signOnBonusValue: null,
  commissionPlan: null,
  brandType: null,
  brandName: null,
  payDescription: null,
  typicalWorkingHours: null,
  paidSickLeave: null,
  hasGrowthOpportunities: null,
  equityOptionsOffered: null,
  buildTeam: null,
  innovativeProduct: null,
  scaleExistingProduct: null,
  buzzwordsCloud: null,
  organizationTypeDescription: null,
  clientContact: null,
  autonomy: null,
  buildLitigationSkills: null,
  lightIndustrialProdType: null,
  apprenticeshipProgram: null,
  advancementOpportunity: null,
  nightShiftPayDifferential: null,
  employmentType: null,
  relocationAssistance: null,
  thirdPartyLinkURL: null,
  thirdPartyLinkText: null,
  productInfo: null,
  salesOpportunityInfo: null,
  compPlanInfo: null,
}

export const getFormattedYearlySalary = (payRange?: string | null, wageType?: GQL.WageType | null) => {
  if (!payRange?.trim()) return ""

  const payRangeParts = payRange?.split("-") || []

  if (payRangeParts.length == 2) {
    const min = getMoneyPerYear(payRangeParts[0]!, wageType)
    const max = getMoneyPerYear(payRangeParts[1]!, wageType)

    return `between $${min.toLocaleString()} and $${max.toLocaleString()} per year`
  }

  return `up to $${getMoneyPerYear(payRangeParts[0]!, wageType).toLocaleString()} per year`
}

const getMoneyPerYear = (value: string, wageType?: GQL.WageType | null) => {
  const intValue = parseInt(value.replace(/[^\d]/g, "")) || 0

  switch (wageType) {
    case "HOURLY":
      return intValue * 168 * 12

    case "MONTHLY":
      return intValue * 12

    case "YEARLY":
    default:
      return intValue
  }
}
